import React, { useCallback, useEffect, useMemo, memo, useState } from 'react';
import qs from 'query-string';
import _get from 'lodash/get';
import * as Sift from '@sift/types';
import usePrevious from 'helpers/usePrevious';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { isNormal, isLoading, useStatus, useData, combineStatuses } from 'resift';
import { useDispatch, useSelector } from 'react-redux';
import useRouter from '@sift/resift/useRouter';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import { Status, States } from '@sift/resift/models/Status';
import showSnackbar from 'helpers/showSnackbar';
import {
  HOME_URL,
  ATTRIBUTES_URL,
  DYNAMIC_PAGES_URL,
  PERSON_PROFILE_URL,
} from 'components/Router/consts';

// Components
import Loader from 'components/Loader';
import AttributesEdit from './AttributesEdit';

// Actions
import makeUpdateCategoryFieldFetch from 'store/attribute/actions/updateCategoryFieldFetch';
import makeCreateCategoryFieldFetch from 'store/attribute/actions/createCategoryFieldFetch';
import makeUpdateLayoutFetch from 'pages/ProfileLayout/fetches/updateLayoutFetch';
import { updateSiftBotFields } from 'store/siftBot';
import makeGetFieldsFetch from 'fetches/makeGetFieldsFetch';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import makeUpdateAttributeAutocomplete from 'fetches/makeUpdateAttributeAutocomplete';
import makeGetAttributeAutocomplete from 'fetches/makeGetAttributeAutocomplete';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import deleteCategoryFieldFetch from 'store/attribute/actions/deleteCategoryFieldFetch';

function Container() {
  const dispatch = useDispatch();
  let { objectKey, entityTypeKey } = useParams<{ objectKey: string; entityTypeKey: string }>();
  const entityTypesFetch = makeEntityTypesFetch();
  const createFieldFetch = makeCreateCategoryFieldFetch();
  const updateFieldFetch = makeUpdateCategoryFieldFetch();
  const updateLayoutFetch = makeUpdateLayoutFetch();
  const updateAttributeAutocompleteFetch = makeUpdateAttributeAutocomplete();
  const getAttributeAutocompleteFetch = makeGetAttributeAutocomplete();
  const updateSiftBotFetch = (...props: any) => updateSiftBotFields(...props);
  const makeGetFields = makeGetFieldsFetch();
  const entityTypeFieldsStatus = useStatus(makeGetFields);
  const entityTypeFields = useData(makeGetFields);
  const makeGetUserFields = makeGetUserFieldsFetch();
  const userFieldsStatus = useStatus(makeGetUserFields);
  const userFields = useData(makeGetUserFields);
  const attributeAutocompleteData = useData(getAttributeAutocompleteFetch);
  const updateFieldFetchStatus = useStatus(updateFieldFetch, { isolatedStatus: true });
  const prevUpdateFieldFetchStatus = usePrevious(updateFieldFetchStatus);
  const createFieldFetchStatus = useStatus(createFieldFetch, { isolatedStatus: true });
  const prevCreateFieldFetchStatus = usePrevious(createFieldFetchStatus);
  const updateLayoutStatus = useStatus(updateLayoutFetch, { isolatedStatus: true });
  const prevUpdateLayoutStatus = usePrevious(updateLayoutStatus);
  const updateSiftBotStatus = useSelector((state: any) => state.siftBot.status.value);
  const prevUpdateSiftBotStatus = usePrevious(updateSiftBotStatus);
  const prevEntityTypeFieldStatus = usePrevious(entityTypeFieldsStatus);
  const prevUserFieldsStatus = usePrevious(userFieldsStatus);
  const updateAutocompleteStatus = useStatus(updateAttributeAutocompleteFetch, {
    isolatedStatus: true,
  });
  const prevUpdateAutocompleteStatus = usePrevious(updateAutocompleteStatus);
  const getAutocompleteStatus = useStatus(getAttributeAutocompleteFetch);

  const entityTypes = useEntityTypes();
  const entityType = _get(entityTypes, entityTypeKey);
  const history = useHistory();
  const { location } = useRouter();
  const userFieldsResults = useMemo(() => _get(userFields, 'results', []), [userFields]);
  const entityTypeFieldsResults = useMemo(() => _get(entityTypeFields, 'results', []), [
    entityTypeFields,
  ]);

  const query = useMemo(() => {
    return qs.parse(location.search);
  }, [location.search]);

  const siftBotFields = useSelector((state: any) => state.siftBot.fields);
  const [attribute, setAttribute] = useState<Sift.Field | undefined>();
  const deleteFieldFetch =
    attribute && attribute.id ? deleteCategoryFieldFetch(attribute.id) : null;
  const deleteFieldStatus = useStatus(deleteFieldFetch);

  useEffect(() => {
    if (!userFields) {
      dispatch(makeGetUserFields());
    }
    if (entityTypeKey !== PERSON_ENTITY_TYPE_KEY && !entityTypeFields) {
      dispatch(makeGetFields(entityTypeKey));
    }
  }, []);

  useEffect(() => {
    if (objectKey) {
      let fields: Sift.Field[] =
        entityTypeKey === PERSON_ENTITY_TYPE_KEY ? userFieldsResults : entityTypeFieldsResults;
      setAttribute(fields.find((field: Sift.Field) => field.objectKey === objectKey));
    }
  }, [entityTypeFieldsResults, userFieldsResults, objectKey]);

  useEffect(() => {
    if (userFields && entityTypeKey === PERSON_ENTITY_TYPE_KEY) {
      const field = userFieldsResults.find((field: Sift.Field) => field.objectKey === objectKey);
      if (_get(field, 'baseField') === 'autocompleteTextCollection') {
        dispatch(getAttributeAutocompleteFetch(field!.id));
      }
    } else if (entityTypeKey !== PERSON_ENTITY_TYPE_KEY && entityTypeFields) {
      const field = userFieldsResults.find((field: Sift.Field) => field.objectKey === objectKey);
      if (_get(field, 'baseField') === 'autocompleteTextCollection') {
        dispatch(getAttributeAutocompleteFetch(field!.id));
      }
    }
  }, [entityTypeFields, userFieldsResults, objectKey]);

  const handleUpdate = useCallback(async (fieldId: string, editedAttribute: any) => {
    await dispatch(updateFieldFetch(fieldId, editedAttribute));
  }, []);

  const handleUpdateLayout = useCallback(payload => {
    dispatch(updateLayoutFetch(payload));
  }, []);

  const handleUpdateSiftBotFields = useCallback(payload => {
    dispatch(updateSiftBotFetch(payload));
  }, []);

  const handleUpdateAttributeAutocomplete = useCallback((fieldId, payload) => {
    dispatch(updateAttributeAutocompleteFetch(fieldId, payload));
  }, []);

  const fieldStatus =
    entityTypeKey === PERSON_ENTITY_TYPE_KEY
      ? userFieldsStatus
      : combineStatuses(entityTypeFieldsStatus, userFieldsStatus);
  const prevFieldStatus =
    entityTypeKey === PERSON_ENTITY_TYPE_KEY
      ? prevUserFieldsStatus
      : combineStatuses(prevEntityTypeFieldStatus, prevUserFieldsStatus);

  useEffect(() => {
    if (
      (prevUpdateFieldFetchStatus &&
        isLoading(prevUpdateFieldFetchStatus) &&
        isNormal(updateFieldFetchStatus)) ||
      (prevCreateFieldFetchStatus &&
        isLoading(prevCreateFieldFetchStatus) &&
        isNormal(createFieldFetchStatus)) ||
      (prevUpdateLayoutStatus &&
        isLoading(prevUpdateLayoutStatus) &&
        isNormal(updateLayoutStatus)) ||
      (prevUpdateSiftBotStatus &&
        isLoading(prevUpdateSiftBotStatus) &&
        isNormal(updateSiftBotStatus)) ||
      (prevFieldStatus && isLoading(prevFieldStatus) && isNormal(fieldStatus)) ||
      (prevUpdateAutocompleteStatus &&
        isLoading(prevUpdateAutocompleteStatus) &&
        isNormal(updateAutocompleteStatus))
    ) {
      const getEntityTypes = async () => {
        await dispatch(entityTypesFetch());
      };
      getEntityTypes();
      if (objectKey.includes('reachOutToMeFor.')) {
        const parentObjectKey = objectKey.split('.')[0];
        history.push(`${HOME_URL}${ATTRIBUTES_URL}/${entityTypeKey}/edit/${parentObjectKey}`);
      } else {
        history.push(
          entityTypeKey === PERSON_ENTITY_TYPE_KEY
            ? `/admin${PERSON_PROFILE_URL}`
            : `${HOME_URL}${DYNAMIC_PAGES_URL}/${entityTypeKey}/attributes`,
        );
      }
    }
  }, [
    updateFieldFetchStatus,
    createFieldFetchStatus,
    updateLayoutStatus,
    updateSiftBotStatus,
    updateAutocompleteStatus,
  ]);

  showSnackbar({
    status: updateFieldFetchStatus,
    message: 'Successfully edited attribute.',
    variant: 'success',
    errorMessage: 'An error occurred while saving your changes.',
  });

  showSnackbar({
    status: createFieldFetchStatus,
    message: 'Successfully created attribute.',
    variant: 'success',
    errorMessage: 'An error occurred while saving your changes.',
  });

  showSnackbar({
    status: combineStatuses(updateAutocompleteStatus, updateLayoutStatus, updateSiftBotStatus),
    message: 'Successfully updated your settings.',
    variant: 'success',
    errorMessage: 'An error occurred while saving your changes.',
  });

  const LOADING = new Status(States.LOADING);
  const NORMAL = new Status(States.NORMAL);
  const entityTypeFieldsExist =
    entityTypeKey === PERSON_ENTITY_TYPE_KEY ? userFields : entityTypeFields && userFields;

  const onLoadStatus = useMemo(
    () =>
      isLoading(
        updateSiftBotStatus,
        updateLayoutStatus,
        updateAutocompleteStatus,
        getAutocompleteStatus,
        deleteFieldStatus,
        entityTypeFieldsStatus,
      ) ||
      !siftBotFields ||
      !entityTypeFieldsExist ||
      !attribute
        ? LOADING
        : NORMAL,
    [
      updateSiftBotStatus,
      updateLayoutStatus,
      updateAutocompleteStatus,
      getAutocompleteStatus,
      deleteFieldStatus,
      entityTypeFieldsStatus,
      siftBotFields,
      entityTypeFieldsExist,
      attribute,
    ],
  );

  return (
    <Loader status={onLoadStatus} style={{ flexDirection: 'row', flex: '1 1 auto' }}>
      {attribute && (
        <AttributesEdit
          attribute={attribute}
          entityType={entityType}
          userFields={userFieldsResults}
          siftBotFields={siftBotFields}
          query={query}
          layoutStatus={combineStatuses(updateLayoutStatus, prevUpdateLayoutStatus)}
          updateField={handleUpdate}
          updateLayout={handleUpdateLayout}
          updateSiftBotFields={handleUpdateSiftBotFields}
          updateAttributeAutocomplete={handleUpdateAttributeAutocomplete}
          attributeAutocompleteData={attributeAutocompleteData}
        />
      )}
    </Loader>
  );
}

export default memo(Container);
